import { AufnahmePunktDocument, bodenformString, wasserformString } from '../../data-model';

/**
 * Wie Haupt- und Feinbodenform angezeigt werden.
 *
 * Die Zusammensetzung steht hier EINMAL, weil sie inzwischen an zwei Stellen
 * gebraucht wird: im Boden-Reiter und im Kopf des Profilbildes. Zwei Fassungen
 * liefen frueher oder spaeter auseinander, und dann zeigte dieselbe Aufnahme
 * zwei verschiedene Bodenformen - ein Fehler, der niemandem auffaellt.
 */

/**
 * Fuehrt ueberhaupt ein Glied der Bodenform diesen Teil?
 *
 * Noetig, weil `bodenformString` das Suffix auch dann anhaengt, wenn KEIN
 * Glied etwas fuehrt. Uebrig blieb dann der blosse Strich des Suffixes, und im
 * Kopf stand „/l57 (s GSP/l)" (Nutzer-Befund 19.09.2026). Gibt es den Teil
 * nicht, gehoert die ganze Angabe weg und nicht nur der Strich.
 */
function hatGlied(bp: AufnahmePunktDocument, welcher: 'hbf' | 'fbf'): boolean {
	return (bp?.header?.bodenform?.bf ?? []).some(p => !!p?.[welcher]);
}

/** Hauptbodenform mit ihrem Suffix, z.B. „s GB". */
export function hauptbodenformText(bp: AufnahmePunktDocument): string {
	if (!hatGlied(bp, 'hbf')) return '';
	const hbf = bodenformString(bp?.header?.bodenform, 'hbf');
	const suffix = bp?.header?.bodenform?.suffix;
	return suffix ? hbf + suffix : hbf;
}

/**
 * Feinbodenform mit allem, was dazugehoert: Wasserform (die Ziffern am Ende,
 * z.B. „HdSB66"), reliktischer Grundgley und Mesoklima.
 */
export function feinbodenformText(bp: AufnahmePunktDocument): string {
	if (!hatGlied(bp, 'fbf')) return '';
	const fbf = bodenformString(bp?.header?.bodenform, 'fbf');
	if (!fbf) return '';
	const ws = wasserformString(bp, true, true);
	const rg = bp?.header?.bodenform?.reliktG ? '(G)' : '';
	const mk = bp?.extendedHeader?.mesoklima ? `-${bp.extendedHeader.mesoklima}` : '';
	return `${fbf}${ws}${rg}${mk}`;
}

/** Eine Zeile der Feinbodenformen-Liste, soweit hier gebraucht. */
export interface FeinbodenformZeile {
	FBF?: string;
	name?: string;
	name_lang?: string;
}

/**
 * „(Teil 1/2)" am Ende eines Namens - eine Angabe ueber den KATALOG, nicht
 * ueber den Boden.
 *
 * Geht ein Steckbrief im SEA ueber zwei Tabellenteile (Seitenzahl „114/1" und
 * „114/2"), heisst der Steckbrief dort „... (Teil 1/2)". Der Zusatz steckt im
 * Namen der Hauptbodenform und wandert ueber `derivedNameLang` (Lokalname +
 * HBF-Langname) in die Langform der Feinbodenform. Im Kopf des Profilbildes
 * gehoert er nicht hin: Dort steht, welcher Boden das ist, nicht, auf welchem
 * Blatt er im Buch steht (Nutzer-Befund 20.09.2026).
 */
const KATALOG_TEIL = /\s*\(Teil\s*\d+\s*\/\s*\d+\)\s*$/;

/**
 * Nachschlag „FBF-Kuerzel -> Langform", z.B. „BgS" -> „Bergrader Sand-Braunerde".
 *
 * Die Liste hat rund 1300 Zeilen; die Zuordnung wird deshalb EINMAL gebaut und
 * nicht bei jedem Blick in den Kopf durchsucht. `name_lang` ist die volle
 * Bezeichnung, `name` nur der Lokalname („Bergrader") - der ist der Rueckfall,
 * wenn die volle Bezeichnung in der Liste fehlt.
 */
export function langformNachschlag(zeilen: FeinbodenformZeile[]): (fbfCode: string) => string | undefined {
	const karte = new Map<string, string>();
	for (const z of zeilen ?? []) {
		const code = (z?.FBF ?? '').trim();
		const text = (z?.name_lang || z?.name || '').trim().replace(KATALOG_TEIL, '');
		if (code && text && !karte.has(code)) karte.set(code, text);
	}
	return code => karte.get((code ?? '').trim());
}

/**
 * Die Langform der Feinbodenform, z.B. „Bergrader Sand-Braunerde".
 *
 * Nachgeschlagen wird das blosse Kuerzel des Gliedes (`bf[].fbf`) - ohne
 * Praefix, Suffix, Wasserform und Mesoklima: In der Liste steht „BgS", auf dem
 * Punkt kann „oBgS6/6-mst" stehen.
 *
 * Bei einer zusammengesetzten Bodenform (begrabener Boden) werden die Glieder
 * mit ihrem Trenner verbunden. **Findet sich fuer EIN Glied keine Langform,
 * kommt gar keine** - eine halb ausgeschriebene Bodenform waere eine Auskunft,
 * die aussieht wie die ganze. Das trifft vor allem Moor-Feinbodenformen: Die
 * werden frei gebildet und stehen in keiner Liste.
 */
export function feinbodenformLangform(
	bp: AufnahmePunktDocument,
	langform: (fbfCode: string) => string | undefined,
): string {
	const glieder = (bp?.header?.bodenform?.bf ?? []).filter(p => !!p?.fbf);
	if (!glieder.length) return '';
	let text = '';
	for (const [i, glied] of glieder.entries()) {
		const name = langform(glied.fbf);
		if (!name) return '';
		if (i === 0) text = name;
		else text += glied.trenner ? ` ${glied.trenner} ${name}` : ` ${name}`;
	}
	return text;
}
